import React, { useState, useEffect } from 'react';
import defaultProfileImage from '../../assets/images/default.png';

// Global image cache and version tracker to prevent duplicate fetches
const imageCache = {
  url: null,
  dataUrl: null,
  lastFetchTime: 0,
  version: 0 // Track version to force refreshes
};

// Function to invalidate cache - call this after successful upload
export const invalidateProfileImageCache = () => {
  imageCache.lastFetchTime = 0;
  imageCache.version++;
  
  // Dispatch event to notify all components to refresh
  window.dispatchEvent(new CustomEvent('profile-image-updated'));
};

/**
 * ProfileImage component with global caching and refresh capability
 */
const ProfileImage = ({ 
  version,
  src,
  defaultSrc = defaultProfileImage, 
  className = '', 
  alt = 'Profile', 
  style = {}, 
  onClick = null 
}) => {
  const [error, setError] = useState(false);
  const [imageUrl, setImageUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cacheVersion, setCacheVersion] = useState(imageCache.version);
  
  // API base URL
  const API_BASE_URL = 'https://phenixloc-api.fnvt.net';
  
  // Get token once on component mount
  const token = useState(() => {
    return sessionStorage.getItem('authToken') || sessionStorage.getItem('userToken');
  })[0];
  
  // Listen for profile image update events
  useEffect(() => {
    const handleProfileImageUpdate = () => {
      setCacheVersion(imageCache.version);
    };
    
    window.addEventListener('profile-image-updated', handleProfileImageUpdate);
    
    return () => {
      window.removeEventListener('profile-image-updated', handleProfileImageUpdate);
    };
  }, []);
  
  // Load the image, refreshing when cacheVersion changes
  useEffect(() => {
    let isMounted = true;
    
    const loadImage = async () => {
      try {
        setLoading(true);
        
        // Determine the base URL without timestamp
        const baseUrl = src || `${API_BASE_URL}/api/user/profile-image`;
        
        // Add a unique identifier based on global cache version and component version
        const versionedUrl = `${baseUrl}?v=${version || ''}&cv=${cacheVersion}`;
        
        // Check if this exact image is already in our cache and not expired
        const now = Date.now();
        if (imageCache.url === baseUrl && 
            imageCache.dataUrl && 
            imageCache.version === cacheVersion && 
            (now - imageCache.lastFetchTime < 60000)) {
          // Use cached version if less than 1 minute old and version matches
          if (isMounted) {
            setImageUrl(imageCache.dataUrl);
            setLoading(false);
          }
          return;
        }
        
        // No valid cache or cache version changed, proceed with fetch
        if (!token) {
          throw new Error('No authentication token');
        }
                
        const response = await fetch(versionedUrl, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (!response.ok) {
          throw new Error(`Failed to load image: ${response.status}`);
        }
        
        const blob = await response.blob();
        
        // Revoke old object URL to prevent memory leaks
        if (imageCache.dataUrl) {
          URL.revokeObjectURL(imageCache.dataUrl);
        }
        
        const objectUrl = URL.createObjectURL(blob);
        
        // Update global cache
        imageCache.url = baseUrl;
        imageCache.dataUrl = objectUrl;
        imageCache.lastFetchTime = now;
        
        if (isMounted) {
          setImageUrl(objectUrl);
          setLoading(false);
        }
      } catch (err) {
        console.error('Error loading profile image:', err);
        if (isMounted) {
          setError(true);
          setLoading(false);
        }
      }
    };
    
    loadImage();
    
    return () => {
      isMounted = false;
    };
  }, [API_BASE_URL, src, version, token, cacheVersion]);
  
  if (loading || error) {
    return (
      <img 
        src={defaultSrc}
        className={className}
        alt={alt}
        style={style}
        onClick={onClick}
      />
    );
  }
  
  return (
    <img
      src={imageUrl}
      className={className}
      alt={alt}
      style={style}
      onClick={onClick}
      onError={() => setError(true)}
    />
  );
};

export default ProfileImage;